/**
 * HTTP 请求工具类
 * 基于 axios 封装的类型安全的 HTTP 客户端
 */
import { logEventSafe } from '@/plugins/firebase'
import axios from 'axios'
import { useLoginDialog } from '@/composables/useLoginDialog'
import type {
  AxiosInstance,
  AxiosRequestConfig,
  AxiosResponse,
  AxiosError,
  InternalAxiosRequestConfig
} from 'axios'
import { setConfig } from './config'
import type {
  BaseResponse,
  RequestConfig,
  RequestInterceptor,
  ResponseInterceptor,
  ApiError,
  BusinessError,
  RequestOptions
} from '@/types/api'
import { HttpStatus } from '@/types/api'
import { tokenStorage, deviceStorage, userStorage } from './storage'
import SignUtils from './sign'
import { AppSignUtils } from './sign'
import { useUserStore } from '@/stores/user'
import { getAdaptiveNetworkManager, recordNetworkError, recordNetworkSuccess } from './adaptiveNetworkManager'

// 默认配置
const DEFAULT_CONFIG = {
  baseURL: import.meta.env.VITE_PUBLIC_API_BASE,
  timeout: 30000, //超时时间20秒
  headers: {
    'Content-Type': 'application/json'
  }
}

// 请求队列配置
const QUEUE_CONFIG = {
  maxConcurrent: 6, // 最大并发请求数
  requestInterval: 50, // 每个请求之间的最小间隔（毫秒）
  maxRetries: 2, // 500错误最大重试次数
  retryDelay: 1000, // 重试基础延迟（毫秒，使用指数退避）
  dedupeWindow: 2000 // 请求去重时间窗口（毫秒）- 在此时间内相同请求会复用结果
}

const { show: showLogin } = useLoginDialog()

// 请求队列项
interface QueueItem {
  config: InternalAxiosRequestConfig
  resolve: (value: any) => void
  reject: (reason?: any) => void
}

// 缓存项
interface CacheItem {
  promise: Promise<any>
  timestamp: number
  result?: any
}

class HttpClient {
  private instance: AxiosInstance
  private requestInterceptors: RequestInterceptor[] = []
  private responseInterceptors: ResponseInterceptor[] = []

  // 请求队列
  private requestQueue: QueueItem[] = []
  // 当前并发请求数
  private activeRequests: number = 0
  // 最后一次请求时间
  private lastRequestTime: number = 0
  // 请求缓存（用于去重，包含时间窗口）
  private requestCache: Map<string, CacheItem> = new Map()

  // 网络自适应配置
  private networkConfig = {
    timeout: 30000,
    maxRetries: 2,
    retryDelay: 1000,
    enableRetry: true
  }

  constructor(config: Partial<AxiosRequestConfig> = {}) {
    this.instance = axios.create({
      ...DEFAULT_CONFIG,
      ...config
    })

    this.setupInterceptors()
    this.setupNetworkAdaptation()
  }

  /**
   * 设置网络自适应功能
   */
  private setupNetworkAdaptation(): void {
    try {
      const networkManager = getAdaptiveNetworkManager()

      // 监听网络配置变化
      networkManager.addConfigListener((config) => {
        this.networkConfig = { ...config }
      })

      // 初始化配置
      this.networkConfig = networkManager.getCurrentAdaptiveConfig()
    } catch (error) {
      console.warn('网络自适应初始化失败:', error)
    }
  }

  /**
   * 设置拦截器
   */
  private setupInterceptors(): void {
    // 请求拦截器
    this.instance.interceptors.request.use(
      (config: InternalAxiosRequestConfig) => {
        // 无网络时静默处理并中断请求
        if (
          typeof navigator !== 'undefined' &&
          'onLine' in navigator &&
          navigator.onLine === false
        ) {
          console.warn('🌐 Network offline, request aborted')
          throw new Error('Network offline')
        }

        // 设置网络自适应超时时间
        if (!config.timeout || config.timeout > this.networkConfig.timeout) {
          config.timeout = this.networkConfig.timeout
        }

        // 添加认证 token
        setConfig(config)
        // 添加请求 ID
        config.headers['X-Request-ID'] = this.generateRequestId()

        // 执行自定义请求拦截器
        for (const interceptor of this.requestInterceptors) {
          if (interceptor.onFulfilled) {
            const result = interceptor.onFulfilled(config as any)
            if (result) {
              config = result as InternalAxiosRequestConfig
            }
          }
        }

        return config
      },
      (error: any) => {
        // 执行自定义请求拦截器错误处理
        for (const interceptor of this.requestInterceptors) {
          if (interceptor.onRejected) {
            error = interceptor.onRejected(error)
          }
        }
        return Promise.reject(error)
      }
    )

    // 响应拦截器
    this.instance.interceptors.response.use(
      (response: AxiosResponse) => {
        // 统一处理 response.data.code 错误
        if (response.data && typeof response.data.code !== 'undefined') {
          const { code, msg } = response.data

          // 如果 code 不等于 200，视为业务错误
          if (code !== 200) {
            const error = this.createBusinessError(code, msg, response)

            // 检查请求配置是否要显示错误
            const config = response.config as any
            const showError = config?.showError !== false

            // 处理特定的业务错误码（显示Toast等）
            if (showError) {
              this.handleBusinessError(code, msg, config.url)
            }

            // 🔧 关键修复：无论是否显示错误，都要抛出异常供业务代码处理
            // 报错出现埋点（仅在显示错误时）
            if (showError) {
              logEventSafe('error_alert_show_!200', {
                code,
                msg: msg || 'Business error',
                url: response.config?.url || 'unknown'
              })
            }
            throw error
          }
        }

        // 记录网络请求成功
        try {
          recordNetworkSuccess()
        } catch (e) {
          // 静默处理
        }

        // 执行自定义响应拦截器
        for (const interceptor of this.responseInterceptors) {
          if (interceptor.onFulfilled) {
            response = interceptor.onFulfilled(response) || response
          }
        }

        return response
      },
      (error: AxiosError) => {
        // 执行自定义响应拦截器错误处理
        for (const interceptor of this.responseInterceptors) {
          if (interceptor.onRejected) {
            error = interceptor.onRejected(error) || error
          }
        }

        return this.handleError(error)
      }
    )
  }

  /**
   * 添加请求拦截器
   */
  addRequestInterceptor(interceptor: RequestInterceptor): void {
    this.requestInterceptors.push(interceptor)
  }

  /**
   * 添加响应拦截器
   */
  addResponseInterceptor(interceptor: ResponseInterceptor): void {
    this.responseInterceptors.push(interceptor)
  }

  /**
   * 生成请求 ID
   */
  private generateRequestId(): string {
    return `req_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`
  }

  /**
   * 生成请求唯一key（用于去重）
   */
  private generateRequestKey(config: InternalAxiosRequestConfig): string {
    const { method, url, params, data } = config
    // 使用 method、url、params、data 生成唯一标识
    const paramsStr = params ? JSON.stringify(params) : ''
    const dataStr = data ? JSON.stringify(data) : ''
    return `${method}:${url}:${paramsStr}:${dataStr}`
  }

  /**
   * 清理过期的缓存
   */
  private cleanExpiredCache(): void {
    const now = Date.now()
    for (const [key, item] of this.requestCache.entries()) {
      if (now - item.timestamp > QUEUE_CONFIG.dedupeWindow) {
        this.requestCache.delete(key)
      }
    }
  }

  /**
   * 将请求加入队列
   */
  private enqueueRequest(config: InternalAxiosRequestConfig): Promise<any> {
    // 生成请求唯一key
    const requestKey = this.generateRequestKey(config)

    // 检查是否允许去重（默认允许）
    const allowDedupe = (config as any).allowDedupe !== false

    if (allowDedupe) {
      // 清理过期缓存
      this.cleanExpiredCache()

      // 检查缓存中是否有在时间窗口内的相同请求
      const cached = this.requestCache.get(requestKey)
      if (cached) {
        const now = Date.now()
        const elapsed = now - cached.timestamp

        // 如果缓存未过期
        if (elapsed < QUEUE_CONFIG.dedupeWindow) {
          console.log(`🔄 Request cache hit (${elapsed}ms ago):`, config.url)

          // 如果已经有结果，直接返回结果
          if (cached.result) {
            return Promise.resolve(cached.result)
          }

          // 否则返回正在进行的Promise
          return cached.promise
        } else {
          // 缓存已过期，删除
          this.requestCache.delete(requestKey)
        }
      }
    }

    // 创建新的请求Promise
    const requestPromise = new Promise((resolve, reject) => {
      this.requestQueue.push({ config, resolve, reject })
      this.processQueue()
    })

    // 保存到缓存
    if (allowDedupe) {
      const cacheItem: CacheItem = {
        promise: requestPromise,
        timestamp: Date.now()
      }

      this.requestCache.set(requestKey, cacheItem)

      // 请求完成后保存结果，但不立即删除缓存
      requestPromise
        .then((result) => {
          // 更新缓存，保存结果
          const item = this.requestCache.get(requestKey)
          if (item) {
            item.result = result
          }

          // 在时间窗口后自动清理
          setTimeout(() => {
            this.requestCache.delete(requestKey)
          }, QUEUE_CONFIG.dedupeWindow)
        })
        .catch(() => {
          // 请求失败，立即删除缓存
          this.requestCache.delete(requestKey)
        })
    }

    return requestPromise
  }

  /**
   * 处理队列中的请求
   */
  private async processQueue(): Promise<void> {
    // 如果没有待处理的请求或已达到最大并发数，直接返回
    if (this.requestQueue.length === 0 || this.activeRequests >= QUEUE_CONFIG.maxConcurrent) {
      return
    }

    // 控制请求间隔
    const now = Date.now()
    const timeSinceLastRequest = now - this.lastRequestTime
    if (timeSinceLastRequest < QUEUE_CONFIG.requestInterval) {
      // 延迟后再处理
      setTimeout(() => this.processQueue(), QUEUE_CONFIG.requestInterval - timeSinceLastRequest)
      return
    }

    // 从队列取出一个请求
    const item = this.requestQueue.shift()
    if (!item) return

    // 增加活动请求计数
    this.activeRequests++
    this.lastRequestTime = Date.now()

    try {
      // 执行请求
      const response = await this.instance.request(item.config)
      item.resolve(response)
    } catch (error: any) {
      // 检查是否是500错误且需要重试
      if (this.shouldRetry(error, item.config)) {
        // 重新加入队列（添加到队列前面，优先处理）
        this.requestQueue.unshift(item)
      } else {
        item.reject(error)
      }
    } finally {
      // 减少活动请求计数
      this.activeRequests--
      // 继续处理队列
      this.processQueue()
    }
  }

  /**
   * 判断是否应该重试
   */
  private shouldRetry(error: any, config: any): boolean {
    const statusCode = error.response?.status

    // 记录网络错误
    try {
      recordNetworkError()
    } catch (e) {
      // 静默处理
    }

    // 检查网络配置是否启用重试
    if (!this.networkConfig.enableRetry) {
      return false
    }

    // 只对500/502/503/504错误重试
    if (statusCode !== HttpStatus.INTERNAL_SERVER_ERROR &&
      statusCode !== HttpStatus.BAD_GATEWAY &&
      statusCode !== HttpStatus.SERVICE_UNAVAILABLE &&
      statusCode !== HttpStatus.GATEWAY_TIMEOUT) {
      return false
    }

    // 初始化重试计数
    if (!config.__retryCount) {
      config.__retryCount = 0
    }

    // 检查是否还可以重试（使用网络配置的重试次数）
    if (config.__retryCount >= this.networkConfig.maxRetries) {
      return false
    }

    // 增加重试计数
    config.__retryCount++

    // 使用网络配置的重试延迟，结合指数退避
    const delay = this.networkConfig.retryDelay * Math.pow(2, config.__retryCount - 1)

    console.log(`Server error (${statusCode}), will retry after ${delay}ms... (${config.__retryCount}/${this.networkConfig.maxRetries})`, config.url)

    // 延迟后再重试
    setTimeout(() => {
      this.processQueue()
    }, delay)

    return true
  }

  /**
   * 创建业务错误
   */
  private createBusinessError(code: number, message: string, response?: any): BusinessError {
    const error: BusinessError = new Error(message || `Business error: ${code}`) as BusinessError
    error.name = 'BusinessError'
    error.code = code
    error.response = response

    // 🔧 添加更多错误信息，方便调试
    error.url = response?.config?.url
    error.method = response?.config?.method?.toUpperCase()
    error.data = response?.data

    return error
  }

  /**
   * 处理错误
   */
  private handleError(error: AxiosError): Promise<never> {
    const apiError: ApiError = {
      code: error.response?.status || HttpStatus.INTERNAL_SERVER_ERROR,
      message: error.message,
      details: error.response?.data,
      timestamp: Date.now()
    }

    // 无网络错误（静默处理，仅记录日志）
    if (typeof navigator !== 'undefined' && 'onLine' in navigator && navigator.onLine === false) {
      console.warn('🌐 Network unavailable:', apiError)
      return Promise.reject(apiError)
    }

    // 处理请求超时错误（静默处理，仅记录日志）
    if (error.code === 'ECONNABORTED' || error.message?.toLowerCase().includes('timeout')) {
      console.warn('⏱️ Request timeout:', apiError)
      return Promise.reject(apiError)
    }

    // API 失败埋点（所有 HTTP 错误）
    const statusCode = error.response?.status || HttpStatus.INTERNAL_SERVER_ERROR

    // 根据状态码处理不同错误
    switch (statusCode) {
      case HttpStatus.UNAUTHORIZED:
        this.handleUnauthorized()
        break
      case HttpStatus.FORBIDDEN:
        this.handleForbidden()
        break
      case HttpStatus.NOT_FOUND:
        this.handleNotFound()
        break
      case HttpStatus.TOO_MANY_REQUESTS:
        this.handleTooManyRequests()
        break
      case HttpStatus.INTERNAL_SERVER_ERROR:
        this.handleServerError()
        break
      default:
        this.handleGenericError(apiError)
    }

    return Promise.reject(apiError)
  }

  /**
   * 处理未授权错误
   */
  private handleUnauthorized(_url?: string): void {
    // 清除用户信息
    const userStore = useUserStore()
    // userStore.logOut()
    // 显示弹窗
    userStore.removeToken()
    showLogin()
  }

  /**
   * 处理禁止访问错误
   */
  private handleForbidden(): void {
    // 🔍 增强日志：记录更多调试信息
    console.warn('🚫 Forbidden (403):', {
      hasToken: !!tokenStorage.getToken(),
      userId: userStorage.getTouristInfo()?.userId,
      udid: deviceStorage.getUdid(),
      timestamp: new Date().toISOString()
    })

    // 报错出现埋点
    logEventSafe('error_alert_show_403', {
      code: HttpStatus.FORBIDDEN,
      msg: 'You do not have permission to access this resource',
      url: 'unknown'
    })
  }

  /**
   * 处理资源不存在错误
   */
  private handleNotFound(): void {
    // 静默处理，仅记录日志和埋点
    console.warn('🔍 Not Found:', HttpStatus.NOT_FOUND)
    // 报错出现埋点
    logEventSafe('error_alert_show_404', {
      code: HttpStatus.NOT_FOUND,
      msg: 'The requested resource does not exist',
      url: 'unknown'
    })
  }

  /**
   * 处理请求过于频繁错误
   */
  private handleTooManyRequests(): void {
    // 静默处理，仅记录日志和埋点
    console.warn('⚠️ Too Many Requests:', HttpStatus.TOO_MANY_REQUESTS)
    // 报错出现埋点
    logEventSafe('error_alert_show_429', {
      code: HttpStatus.TOO_MANY_REQUESTS,
      msg: 'The requests are too frequent. Please try again later',
      url: 'unknown'
    })
  }

  /**
   * 处理服务器错误
   */
  private handleServerError(): void {
    // 静默处理，仅记录日志和埋点
    console.warn('💥 Server Error:', HttpStatus.INTERNAL_SERVER_ERROR)
    // 报错出现埋点
    logEventSafe('error_alert_show_500', {
      code: HttpStatus.INTERNAL_SERVER_ERROR,
      msg: 'There is an internal error on the server. Please try again later',
      url: 'unknown'
    })
  }

  /**
   * 处理通用错误
   */
  private handleGenericError(error: ApiError): void {
    const message = error.details?.message || error.message || 'Request failed'
    // 静默处理，仅记录日志和埋点
    console.warn('❌ Generic Error:', { code: error.code, message })
    // 报错出现埋点
    logEventSafe('error_alert_show', {
      code: error.code,
      msg: message,
      url: 'unknown'
    })
  }

  /**
   * 处理业务错误码
   */
  private handleBusinessError(code: number, message: string, url: string): void {
    switch (code) {
      case 650:
      case 660:
      case 670:
        // 未授权错误 - 保留登录弹窗（业务逻辑）
        this.handleUnauthorized(url)
        logEventSafe(`error_alert_show_popup${userStorage.getTouristInfo()?.userId}`)
        break
      case 800:
        // 静默处理，仅记录日志和埋点
        console.warn('🔐 Login required:', code)
        logEventSafe('error_alert_show_800', {
          code,
          msg: 'Please login again',
          url
        })
        break
      // 参数错误
      case 400:
      case 2001:
      case 2002:
        console.warn('📝 Parameter error:', { code, message })
        break

      // 资源不存在
      case 404:
      case 3001:
        console.warn('🔍 Resource not found:', { code, message })
        break

      // 业务逻辑错误
      case 4001:
      case 4002:
      case 4003:
      case 1004: // 账户被锁定
      case 1005: // 账户已注销
        console.warn('⚠️ Business logic error:', { code, message })
        break

      // 数据验证错误
      case 2003:
      case 2004:
      case 2005:
        console.warn('🔧 Data validation error:', { code, message })
        break

      // 余额不足等支付相关错误 - 保留充值弹窗（业务逻辑）
      case 730:
      case 7002:
      case 7003:
        console.warn('💰 Insufficient balance:', code)
        // 弹出StoreCom组件进行充值
        setTimeout(() => {
          logEventSafe('store_show_up')
          // 使用全局弹窗管理器
          if (typeof window !== 'undefined' && (window as any).openStoreDialog) {
            ;(window as any).openStoreDialog('insufficient_balance')
          }
        }, 10)
        break

      // 限流错误
      case 429:
      case 5001:
        console.warn('⚠️ Rate limit:', { code, message })
        break

      // 服务器错误
      case 500:
      case 6001:
      case 6002:
        console.warn('💥 Server error:', { code, message })
        break

      // 其他业务错误
      default:
        console.warn('🔥 未处理的业务错误码:', { code, message })
        break
    }
  }

  /**
   * 通用请求方法
   */
  async request<T = any>(
    config: RequestConfig,
    options: RequestOptions = {}
  ): Promise<BaseResponse<T>> {
    const { showError = true, timeout = 30000, headers = {}, signal } = options

    try {
      const axiosConfig: AxiosRequestConfig = {
        url: config.url,
        method: config.method,
        data: config.data,
        params: config.params,
        headers: { ...headers, ...config.headers },
        timeout,
        signal,
        // 传递 showError 配置到请求中，供拦截器使用
        showError
      } as any

      // 检查是否使用队列（默认使用）
      const useQueue = (config as any).useQueue !== false

      let response: AxiosResponse<BaseResponse<T>>

      if (useQueue) {
        // 通过队列执行请求
        response = await this.enqueueRequest(axiosConfig as InternalAxiosRequestConfig)
      } else {
        // 直接执行请求
        response = await this.instance.request<BaseResponse<T>>(axiosConfig)
      }

      return response.data
    } catch (error) {
      if (showError && error instanceof Error) {
        // 错误处理已在拦截器中完成
      }

      throw error
    }
  }

  /**
   * GET 请求
   */
  async get<T = any>(
    url: string,
    params?: Record<string, any>,
    options?: RequestOptions
  ): Promise<BaseResponse<T>> {
    return this.request<T>(
      {
        url,
        method: 'GET',
        params
      },
      options
    )
  }

  /**
   * POST 请求
   */
  async post<T = any>(url: string, data?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>(
      {
        url,
        method: 'POST',
        data
      },
      options
    )
  }

  /**
   * PUT 请求
   */
  async put<T = any>(url: string, data?: any, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>(
      {
        url,
        method: 'PUT',
        data
      },
      options
    )
  }

  /**
   * DELETE 请求
   */
  async delete<T = any>(url: string, options?: RequestOptions): Promise<BaseResponse<T>> {
    return this.request<T>(
      {
        url,
        method: 'DELETE'
      },
      options
    )
  }

  /**
   * PATCH 请求
   */
  async patch<T = any>(
    url: string,
    data?: any,
    options?: RequestOptions
  ): Promise<BaseResponse<T>> {
    return this.request<T>(
      {
        url,
        method: 'PATCH',
        data
      },
      options
    )
  }

  /**
   * 上传文件
   */
  async upload<T = any>(
    url: string,
    image: File,
    options?: RequestOptions & {
      onProgress?: (progress: number) => void
      fieldName?: string
    }
  ): Promise<BaseResponse<T>> {
    const formData = new FormData()
    const sign = SignUtils.generateApiSignature(
      tokenStorage.getToken() || '',
      userStorage.getTouristInfo()?.userId || '',
      AppSignUtils.APP_SECRET || ''
    )

    formData.append(options?.fieldName || 'image', image)
    formData.append('udid', deviceStorage.getUdid() || '')
    formData.append('appId', '8e7351e-10cfda-aichat-aishmin-ios')
    formData.append('sign', sign)
    return this.request<T>(
      {
        url,
        method: 'POST',
        data: formData,
        headers: {
          'Content-Type': 'multipart/form-data'
        }
      },
      {
        ...options,
        timeout: 30000 // 上传文件超时时间更长
      }
    )
  }

  /**
   * 下载文件
   */
  async download(url: string, filename?: string, options?: RequestOptions): Promise<void> {
    try {
      const response = await this.instance.get(url, {
        responseType: 'blob',
        ...options
      })

      const blob = new Blob([response.data])
      const downloadUrl = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = downloadUrl
      link.download = filename || 'download'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.URL.revokeObjectURL(downloadUrl)
    } catch (error) {
      throw error
    }
  }

  /**
   * 取消请求
   */
  cancelRequest(_requestId: string): void {
    // 这里可以实现请求取消逻辑
    // 需要维护一个请求 ID 到 AbortController 的映射
  }

  /**
   * 取消所有请求
   */
  cancelAllRequests(): void {
    // 这里可以实现取消所有请求的逻辑
  }

  /**
   * 获取 axios 实例
   */
  getInstance(): AxiosInstance {
    return this.instance
  }

  /**
   * 更新基础配置
   */
  updateConfig(config: Partial<AxiosRequestConfig>): void {
    Object.assign(this.instance.defaults, config)
  }

  /**
   * 判断是否为业务错误
   */
  static isBusinessError(error: any): error is BusinessError {
    return error && error.name === 'BusinessError' && typeof error.code === 'number'
  }
}

// 创建默认实例
const httpClient = new HttpClient()

// 工具函数
export const isBusinessError = HttpClient.isBusinessError

/**
 * 提取错误信息
 */
export function getErrorMessage(error: any): string {
  if (isBusinessError(error)) {
    return error.message || '请求失败'
  }
  if (error instanceof Error) {
    return error.message
  }
  return '未知错误'
}

/**
 * 提取错误码
 */
export function getErrorCode(error: any): number | null {
  if (isBusinessError(error)) {
    return error.code
  }
  return null
}

// 导出实例和类
export { HttpClient, httpClient }

// 🧪 测试函数：验证错误是否正确抛出
export function testErrorHandling() {
  console.log('🧪 测试错误处理机制...')

  // 模拟一个 code !== 200 的响应
  const mockResponse = {
    data: {
      code: 4001,
      message: '测试业务错误'
    },
    config: {
      url: '/test/api',
      method: 'POST'
    }
  }

  try {
    const httpClientInstance = new HttpClient()
    const error = (httpClientInstance as any).createBusinessError(
      mockResponse.data.code,
      mockResponse.data.message,
      mockResponse
    )
    console.log('✅ BusinessError 创建成功:', error)
    console.log('✅ 错误属性:', {
      name: error.name,
      code: error.code,
      message: error.message,
      url: error.url,
      method: error.method
    })
    return true
  } catch (e) {
    console.error('❌ 错误处理测试失败:', e)
    return false
  }
}
export default httpClient
